import React, { useRef } from 'react';
import { AlignCenter, AlignLeft, AlignRight, Bold, ChevronDown, ChevronUp, ImageIcon, Italic, Trash2, Underline } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { applyText, firstRun, styleParagraphs, styleRuns, textOf } from './studioText';
import type { SlideBackground, SlideElement } from '../types';

interface Props {
  element: SlideElement | null;
  background: SlideBackground | null | undefined;
  disabled?: boolean;
  onChange: (element: SlideElement) => void;
  onDelete: () => void;
  onReorder: (direction: 'forward' | 'backward') => void;
  onBackgroundChange: (background: SlideBackground | null) => void;
  onPickImage: (purpose: 'element' | 'background', file: File) => void;
}

const number = (value: string, fallback: number) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

function ColorField({ label, value, onChange, disabled }: { label: string; value: string | null | undefined; onChange: (value: string) => void; disabled?: boolean }) {
  return (
    <label className="flex items-center justify-between gap-2 text-sm">
      <span>{label}</span>
      <input
        type="color" aria-label={label} value={value || '#000000'} disabled={disabled}
        onChange={(event) => onChange(event.target.value)} className="h-8 w-12 cursor-pointer rounded border border-slate-200"
      />
    </label>
  );
}

function NumberField({ label, value, onChange, disabled, step = 1, min }: { label: string; value: number; onChange: (value: number) => void; disabled?: boolean; step?: number; min?: number }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input
        type="number" aria-label={label} value={Number.isFinite(value) ? Math.round(value * 100) / 100 : 0} step={step} min={min}
        disabled={disabled} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(number(event.target.value, value))}
        className="h-8"
      />
    </div>
  );
}

/** Opmaak- en eigenschappenpaneel voor het geselecteerde element, of voor de achtergrond zonder selectie. */
export default function ElementInspector({
  element, background, disabled, onChange, onDelete, onReorder, onBackgroundChange, onPickImage,
}: Props) {
  const imageInput = useRef<HTMLInputElement>(null);
  const purpose = useRef<'element' | 'background'>('element');

  const pickFile = (target: 'element' | 'background') => {
    purpose.current = target;
    imageInput.current?.click();
  };

  const fileInput = (
    <input
      ref={imageInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" aria-label="Afbeeldingsbestand"
      onChange={(event) => {
        const file = event.target.files?.[0];
        if (file) onPickImage(purpose.current, file);
        event.target.value = '';
      }}
    />
  );

  if (!element) {
    return (
      <div className="space-y-3" aria-label="Achtergrond">
        <h3 className="text-sm font-semibold text-slate-900">Achtergrond</h3>
        <ColorField
          label="Achtergrondkleur" value={background?.color || '#ffffff'} disabled={disabled}
          onChange={(color) => onBackgroundChange({ ...(background || {}), color })}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => pickFile('background')}>
            <ImageIcon className="mr-1 h-4 w-4" />
            {background?.media_id ? 'Achtergrondafbeelding vervangen' : 'Achtergrondafbeelding kiezen'}
          </Button>
          {background?.media_id && (
            <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onBackgroundChange({ ...background, media_id: null })}>
              Afbeelding verwijderen
            </Button>
          )}
        </div>
        {fileInput}
        <p className="text-xs text-slate-500">Klik op een onderdeel van de slide om het te bewerken.</p>
      </div>
    );
  }

  const run = firstRun(element);
  const align = element.text?.paragraphs[0]?.align || 'left';
  const hasText = element.text !== undefined && element.text !== null && element.type !== 'image';

  return (
    <div className="space-y-4" aria-label="Eigenschappen">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">
          {element.type === 'text' ? 'Tekst' : element.type === 'image' ? 'Afbeelding' : 'Vorm'}
        </h3>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="icon" aria-label="Naar voren" disabled={disabled} onClick={() => onReorder('forward')}>
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" aria-label="Naar achteren" disabled={disabled} onClick={() => onReorder('backward')}>
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" aria-label="Onderdeel verwijderen" disabled={disabled} onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {hasText && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="studio-text" className="text-xs text-slate-500">Tekst</Label>
            <Textarea
              id="studio-text" aria-label="Tekstinhoud" rows={4} disabled={disabled} value={textOf(element)}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(applyText(element, event.target.value))}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="Lettergrootte" value={(run?.font_size || 28)} min={1} disabled={disabled}
              onChange={(value) => onChange(styleRuns(element, { font_size: Math.max(1, value) }))}
            />
            <ColorField label="Tekstkleur" value={run?.color} disabled={disabled} onChange={(color) => onChange(styleRuns(element, { color }))} />
          </div>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Opmaak">
            <Button type="button" size="icon" variant={run?.bold ? 'default' : 'outline'} aria-label="Vet" aria-pressed={!!run?.bold} disabled={disabled} onClick={() => onChange(styleRuns(element, { bold: !run?.bold }))}>
              <Bold className="h-4 w-4" />
            </Button>
            <Button type="button" size="icon" variant={run?.italic ? 'default' : 'outline'} aria-label="Cursief" aria-pressed={!!run?.italic} disabled={disabled} onClick={() => onChange(styleRuns(element, { italic: !run?.italic }))}>
              <Italic className="h-4 w-4" />
            </Button>
            <Button type="button" size="icon" variant={run?.underline ? 'default' : 'outline'} aria-label="Onderstrepen" aria-pressed={!!run?.underline} disabled={disabled} onClick={() => onChange(styleRuns(element, { underline: !run?.underline }))}>
              <Underline className="h-4 w-4" />
            </Button>
            {([['left', AlignLeft, 'Links uitlijnen'], ['center', AlignCenter, 'Centreren'], ['right', AlignRight, 'Rechts uitlijnen']] as const).map(([value, Icon, label]) => (
              <Button
                key={value} type="button" size="icon" variant={align === value ? 'default' : 'outline'} aria-label={label}
                aria-pressed={align === value} disabled={disabled} onClick={() => onChange(styleParagraphs(element, { align: value }))}
              >
                <Icon className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </div>
      )}

      {element.type === 'shape' && element.shape !== 'line' && (
        <div className="space-y-2">
          <ColorField label="Vulkleur" value={element.fill} disabled={disabled} onChange={(fill) => onChange({ ...element, fill })} />
          <ColorField label="Randkleur" value={element.stroke} disabled={disabled} onChange={(stroke) => onChange({ ...element, stroke, stroke_width: element.stroke_width || 2 })} />
          <NumberField label="Randdikte" value={element.stroke_width || 0} min={0} disabled={disabled} onChange={(value) => onChange({ ...element, stroke_width: Math.max(0, value) })} />
          <NumberField label="Dekking (0-1)" value={element.opacity ?? 1} step={0.1} min={0} disabled={disabled} onChange={(value) => onChange({ ...element, opacity: Math.min(1, Math.max(0, value)) })} />
        </div>
      )}

      {element.type === 'shape' && element.shape === 'line' && (
        <ColorField label="Lijnkleur" value={element.stroke} disabled={disabled} onChange={(stroke) => onChange({ ...element, stroke })} />
      )}

      {element.type === 'image' && (
        <div className="space-y-2">
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => pickFile('element')}>
            <ImageIcon className="mr-1 h-4 w-4" />
            Afbeelding vervangen
          </Button>
          <label className="flex items-center justify-between gap-2 text-sm">
            <span>Passend maken</span>
            <select
              aria-label="Passend maken" value={element.fit || 'fill'} disabled={disabled} className="rounded border border-slate-200 px-2 py-1"
              onChange={(event) => onChange({ ...element, fit: event.target.value as SlideElement['fit'] })}
            >
              <option value="fill">Uitrekken</option>
              <option value="contain">Binnen kader</option>
              <option value="cover">Kader vullen</option>
            </select>
          </label>
        </div>
      )}
      {fileInput}

      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Links (x)" value={element.x} disabled={disabled} onChange={(x) => onChange({ ...element, x })} />
        <NumberField label="Boven (y)" value={element.y} disabled={disabled} onChange={(y) => onChange({ ...element, y })} />
        <NumberField label="Breedte" value={element.width} min={5} disabled={disabled} onChange={(width) => onChange({ ...element, width: Math.max(5, width) })} />
        <NumberField label="Hoogte" value={element.height} min={5} disabled={disabled} onChange={(height) => onChange({ ...element, height: Math.max(5, height) })} />
        <NumberField label="Rotatie (graden)" value={element.rotation || 0} disabled={disabled} onChange={(rotation) => onChange({ ...element, rotation: Math.max(-360, Math.min(360, rotation)) })} />
      </div>
    </div>
  );
}
